'use client';

import { useEffect, useId, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Anchor,
  Building2,
  Briefcase,
  Eye,
  EyeOff,
  GraduationCap,
  Loader2,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  ArrowRight,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { handleApiError } from '@/lib/api/client';
import { toast } from 'sonner';

export type Role = 'seafarer' | 'shore' | 'employer' | 'institute';

export const roles = [
  {
    value: 'seafarer' as Role,
    icon: Anchor,
    title: 'Seafarer',
    subtitle: 'Deck, Engine, Catering & Ratings',
    description: 'Find onboard positions matched to your rank and experience.',
    color: 'border-neon-cyan/30 bg-neon-cyan/5',
    iconColor: 'text-neon-cyan',
    iconBg: 'bg-neon-cyan/10',
  },
  {
    value: 'shore' as Role,
    icon: Briefcase,
    title: 'Shore Professional',
    subtitle: 'Fleet Management, Operations & Technical',
    description: 'Explore shore-based maritime roles and transition from sea to shore.',
    color: 'border-neon-green/30 bg-neon-green/5',
    iconColor: 'text-neon-green',
    iconBg: 'bg-neon-green/10',
  },
  {
    value: 'employer' as Role,
    icon: Building2,
    title: 'Employer / Manning Agent',
    subtitle: 'Ship Owners, Managers & Agencies',
    description: 'Post jobs, search verified candidates, and manage your crew pipeline.',
    color: 'border-neon-purple/30 bg-neon-purple/5',
    iconColor: 'text-neon-purple',
    iconBg: 'bg-neon-purple/10',
  },
  {
    value: 'institute' as Role,
    icon: GraduationCap,
    title: 'Training Institute',
    subtitle: 'Maritime Courses & Certification',
    description: 'Publish course offerings, manage batches, and collect student enquiries.',
    color: 'border-neon-amber/30 bg-neon-amber/5',
    iconColor: 'text-neon-amber',
    iconBg: 'bg-neon-amber/10',
  },
];

function buildDetailsSchema(role: Role) {
  const phoneSchema =
    role === 'shore'
      ? z.string().optional()
      : z.string().min(1, 'Phone number is required');
  return z.object({
    firstName: z.string().min(1, 'First name is required').max(50),
    lastName: z.string().min(1, 'Last name is required').max(50),
    email: z.string().min(1, 'Email is required').email('Enter a valid email'),
    password: z
      .string()
      .min(8, 'Use at least 8 characters.')
      .regex(/[A-Z]/, 'Add at least 1 capital letter.')
      .regex(/[0-9]/, 'Add at least 1 number.'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
    phone: phoneSchema,
    companyName: z.string().optional(),
    instituteName: z.string().optional(),
    agreedToTerms: z.literal(true, {
      errorMap: () => ({ message: 'You must agree to the terms' }),
    }),
  }).refine((d) => d.password === d.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  }).refine((d) => {
    if (role === 'institute') {
      return (d.instituteName ?? '').trim().length > 0;
    }
    return true;
  }, {
    message: 'Institute name is required',
    path: ['instituteName'],
  });
}

type DetailsFormData = z.infer<ReturnType<typeof buildDetailsSchema>>;

export function RoleStep({
  selectedRole,
  onSelect,
}: {
  selectedRole?: Role | null;
  onSelect: (role: Role) => void;
}) {
  const [hovered, setHovered] = useState<Role | null>(null);

  return (
    <div className="mx-auto w-full max-w-3xl">
      <p className="mb-5 text-sm font-medium text-ash">
        Choose your path to get started.
      </p>

      <div className="space-y-3">
        {roles.map((role) => {
          const Icon = role.icon;
          const isHovered = hovered === role.value;
          const isSelected = selectedRole === role.value;
          return (
            <button
              key={role.value}
              onClick={() => onSelect(role.value)}
              onMouseEnter={() => setHovered(role.value)}
              onMouseLeave={() => setHovered(null)}
              className={`group w-full rounded-2xl border-2 p-5 text-left transition-all duration-200 ${
                isSelected || isHovered
                  ? role.color + ' shadow-md scale-[1.01]'
                  : 'border-neon-cyan/10 bg-card hover:border-neon-cyan/25'
              }`}
              aria-pressed={isSelected}
            >
              <div className="flex items-center gap-4">
                <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${isSelected || isHovered ? role.iconBg : 'bg-panel'} transition-colors`}>
                  <Icon className={`h-6 w-6 ${isSelected || isHovered ? role.iconColor : 'text-ash'} transition-colors`} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-semibold text-foreground">{role.title}</h3>
                    {isSelected && (
                      <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-primary">
                        Selected
                      </span>
                    )}
                  </div>
                  <p className="text-xs font-medium text-ash">{role.subtitle}</p>
                  <p className={`mt-1 text-xs text-ash transition-all ${isSelected || isHovered ? 'max-h-24 opacity-100' : 'max-h-0 overflow-hidden opacity-0 sm:max-h-24 sm:opacity-100'}`}>
                    {role.description}
                  </p>
                </div>
                <ArrowRight className={`h-5 w-5 shrink-0 transition-all ${isSelected || isHovered ? role.iconColor : 'text-muted'}`} />
              </div>
            </button>
          );
        })}
      </div>

      <p className="mt-6 text-center text-sm text-ash">
        Already have an account?{" "}
        <Link href="/login" className="font-medium text-foreground hover:underline">
          Sign in
        </Link>
      </p>
    </div>
  );
}

function DetailsStep({
  role,
  onBack,
  onSuccess,
}: {
  role: Role;
  onBack: () => void;
  onSuccess: () => void;
}) {
  const { register: registerUser, isAuthenticating } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const baseId = useId();
  const firstNameId = `${baseId}-firstName`;
  const lastNameId = `${baseId}-lastName`;
  const phoneId = `${baseId}-phone`;
  const emailId = `${baseId}-email`;
  const companyNameId = `${baseId}-companyName`;
  const instituteNameId = `${baseId}-instituteName`;
  const passwordId = `${baseId}-password`;
  const confirmPasswordId = `${baseId}-confirmPassword`;
  const agreedToTermsId = `${baseId}-agreedToTerms`;

  const errorIdFor = (fieldId: string) => `${fieldId}-error`;

  const selectedRole = roles.find((r) => r.value === role)!;
  const Icon = selectedRole.icon;

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<DetailsFormData>({
    resolver: zodResolver(buildDetailsSchema(role)),
  });

  const submitting = isSubmitting || isAuthenticating;

  const onSubmit = async (data: DetailsFormData) => {
    setApiError(null);
    try {
      await registerUser({
        email: data.email,
        password: data.password,
        confirmPassword: data.confirmPassword,
        firstName: data.firstName,
        lastName: data.lastName,
        role,
        phone: data.phone,
        companyName: role === 'employer' ? data.companyName : undefined,
        instituteName: role === 'institute' ? data.instituteName : undefined,
        agreedToTerms: true,
      });
      toast.success('Account created successfully');
      onSuccess();
    } catch (err) {
      const error = handleApiError(err);
      toast.error(error.message || 'Registration failed');

      if (error.details) {
        const fieldMap: Record<string, keyof DetailsFormData> = {
          email: 'email',
          password: 'password',
          first_name: 'firstName',
          last_name: 'lastName',
          role: 'firstName',
          company_name: 'companyName',
          institute_name: 'instituteName',
          phone: 'phone',
        };

        Object.entries(error.details).forEach(([apiField, messages]) => {
          const formField = fieldMap[apiField];
          if (formField && Array.isArray(messages) && messages.length > 0) {
            setError(formField, { message: messages[0] });
          }
        });
      }

      if (!error.details || Object.keys(error.details).length === 0) {
          setApiError(error.message || 'We could not create your account. Please try again.');
      } else {
        setApiError(null);
      }
    }
  };

  return (
    <div className="mx-auto w-full max-w-xl">
      <div className="mb-6 flex items-center gap-3">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-ash hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
        <div className="flex items-center gap-2 rounded-full border border-neon-cyan/10 bg-panel px-3 py-1">
          <Icon className={`h-3.5 w-3.5 ${selectedRole.iconColor}`} />
          <span className="text-xs font-medium text-foreground">{selectedRole.title}</span>
        </div>
      </div>

      <h2 className="text-xl font-bold text-foreground">Create your account</h2>
      <p className="mt-1 text-sm text-ash">
        {role === 'employer'
          ? 'Set up your company account to start hiring'
          : role === 'shore'
          ? 'Join thousands of shore professionals on CrewAhead'
          : role === 'institute'
          ? 'Register your maritime training institute'
          : "Join the world's leading maritime career platform"}
      </p>

      {apiError && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-500/20 bg-red-500/5 px-4 py-3 text-sm text-red-400">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          {apiError}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor={firstNameId} className="block text-sm font-medium text-foreground">First name</label>
            <input
              id={firstNameId}
              autoComplete="given-name"
              aria-invalid={errors.firstName ? 'true' : 'false'}
              aria-describedby={errors.firstName ? errorIdFor(firstNameId) : undefined}
              {...register('firstName')}
              placeholder="John"
              className="mt-1 block w-full rounded-lg border border-white/10 bg-obsidian px-3 py-2.5 text-sm text-white focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
            {errors.firstName && <p id={errorIdFor(firstNameId)} className="mt-1 text-xs text-red-400">{errors.firstName.message}</p>}
          </div>
          <div>
            <label htmlFor={lastNameId} className="block text-sm font-medium text-foreground">Last name</label>
            <input
              id={lastNameId}
              autoComplete="family-name"
              aria-invalid={errors.lastName ? 'true' : 'false'}
              aria-describedby={errors.lastName ? errorIdFor(lastNameId) : undefined}
              {...register('lastName')}
              placeholder="Smith"
              className="mt-1 block w-full rounded-lg border border-white/10 bg-obsidian px-3 py-2.5 text-sm text-white focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
            {errors.lastName && <p id={errorIdFor(lastNameId)} className="mt-1 text-xs text-red-400">{errors.lastName.message}</p>}
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label htmlFor={phoneId} className="block text-sm font-medium text-foreground">
              Phone number
              {role !== 'shore' && <span className="text-red-400">*</span>}
            </label>
            <input
              id={phoneId}
              type="tel"
              autoComplete="tel"
              aria-invalid={errors.phone ? 'true' : 'false'}
              aria-describedby={errors.phone ? errorIdFor(phoneId) : undefined}
              {...register('phone')}
              placeholder="+44 20 7946 0958"
              className="mt-1 block w-full rounded-lg border border-white/10 bg-obsidian px-3 py-2.5 text-sm text-white focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
            {errors.phone && <p id={errorIdFor(phoneId)} className="mt-1 text-xs text-red-400">{errors.phone.message}</p>}
          </div>

          <div>
            <label htmlFor={emailId} className="block text-sm font-medium text-foreground">Email address</label>
            <input
              id={emailId}
              type="email"
              autoComplete="email"
              aria-invalid={errors.email ? 'true' : 'false'}
              aria-describedby={errors.email ? errorIdFor(emailId) : undefined}
              {...register('email')}
              placeholder={role === 'employer' ? 'you@company.com' : 'you@example.com'}
              className="mt-1 block w-full rounded-lg border border-white/10 bg-obsidian px-3 py-2.5 text-sm text-white focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
            {errors.email && <p id={errorIdFor(emailId)} className="mt-1 text-xs text-red-400">{errors.email.message}</p>}
          </div>
        </div>

        {role === 'employer' && (
          <div>
            <label htmlFor={companyNameId} className="block text-sm font-medium text-foreground">
              Company name <span className="text-muted">(optional)</span>
            </label>
            <input
              id={companyNameId}
              autoComplete="organization"
              aria-invalid={errors.companyName ? 'true' : 'false'}
              aria-describedby={errors.companyName ? errorIdFor(companyNameId) : undefined}
              {...register('companyName')}
              placeholder="e.g. Pacific Maritime Ltd"
              className="mt-1 block w-full rounded-lg border border-white/10 bg-obsidian px-3 py-2.5 text-sm text-white focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
            {errors.companyName && <p id={errorIdFor(companyNameId)} className="mt-1 text-xs text-red-400">{errors.companyName.message}</p>}
          </div>
        )}

        {role === 'institute' && (
          <div>
            <label htmlFor={instituteNameId} className="block text-sm font-medium text-foreground">
              Institute name <span className="text-red-400">*</span>
            </label>
            <input
              id={instituteNameId}
              autoComplete="organization"
              aria-invalid={errors.instituteName ? 'true' : 'false'}
              aria-describedby={errors.instituteName ? errorIdFor(instituteNameId) : undefined}
              {...register('instituteName')}
              placeholder="e.g. Maritime Training Centre"
              className="mt-1 block w-full rounded-lg border border-white/10 bg-obsidian px-3 py-2.5 text-sm text-white focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
            {errors.instituteName && <p id={errorIdFor(instituteNameId)} className="mt-1 text-xs text-red-400">{errors.instituteName.message}</p>}
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label htmlFor={passwordId} className="block text-sm font-medium text-foreground">Password</label>
            <div className="relative mt-1">
              <input
                id={passwordId}
                autoComplete="new-password"
                aria-invalid={errors.password ? 'true' : 'false'}
                aria-describedby={errors.password ? errorIdFor(passwordId) : undefined}
                {...register('password')}
                type={showPassword ? 'text' : 'password'}
                placeholder="Min. 8 characters"
                className="block w-full rounded-lg border border-white/10 bg-obsidian px-3 py-2.5 pr-10 text-sm text-white focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-ash hover:text-foreground"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.password && <p id={errorIdFor(passwordId)} className="mt-1 text-xs text-red-400">{errors.password.message}</p>}
          </div>

          <div>
            <label htmlFor={confirmPasswordId} className="block text-sm font-medium text-foreground">Confirm password</label>
            <div className="relative mt-1">
              <input
                id={confirmPasswordId}
                autoComplete="new-password"
                aria-invalid={errors.confirmPassword ? 'true' : 'false'}
                aria-describedby={errors.confirmPassword ? errorIdFor(confirmPasswordId) : undefined}
                {...register('confirmPassword')}
                type={showConfirm ? 'text' : 'password'}
                placeholder="Repeat your password"
                className="block w-full rounded-lg border border-white/10 bg-obsidian px-3 py-2.5 pr-10 text-sm text-white focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-ash hover:text-foreground"
                aria-label={showConfirm ? 'Hide confirm password' : 'Show confirm password'}
              >
                {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.confirmPassword && <p id={errorIdFor(confirmPasswordId)} className="mt-1 text-xs text-red-400">{errors.confirmPassword.message}</p>}
          </div>
        </div>

        <label htmlFor={agreedToTermsId} className="flex cursor-pointer items-start gap-3">
          <input
            id={agreedToTermsId}
            type="checkbox"
            {...register('agreedToTerms')}
            className="mt-0.5 h-4 w-4 rounded border-white/10 bg-obsidian text-primary focus:ring-primary"
          />
          <span className="text-xs text-ash">
            I agree to CrewAhead&apos;s{" "}
            <Link href="/terms" className="text-foreground underline">Terms of Service</Link>{" "}
            and{" "}
            <Link href="/privacy" className="text-foreground underline">Privacy Policy</Link>
          </span>
        </label>
        {errors.agreedToTerms && <p id={errorIdFor(agreedToTermsId)} className="text-xs text-red-400">{errors.agreedToTerms.message}</p>}

        <button
          type="submit"
          disabled={submitting}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary py-2.5 text-sm font-semibold text-obsidian transition-all hover:bg-primary/90 disabled:opacity-60"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Create Account
        </button>
      </form>

      <p className="mt-4 text-center text-sm text-ash">
        Already have an account?{" "}
        <Link href="/login" className="font-medium text-foreground hover:underline">
          Sign in
        </Link>
      </p>
    </div>
  );
}

function SuccessStep({ role }: { role: Role }) {
  const router = useRouter();
  const selectedRole = roles.find((r) => r.value === role)!;
  const Icon = selectedRole.icon;

  const isInstitute = role === 'institute';
  const nextHref = isInstitute ? '/institute/pending' : '/step-1';
  const ctaLabel = isInstitute ? 'Continue to Registration Status' : 'Set Up My Profile';

  return (
    <div className="mx-auto w-full max-w-xl text-center">
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-neon-green/10">
        <CheckCircle className="h-8 w-8 text-neon-green" />
      </div>
      <h2 className="text-2xl font-bold text-foreground">You&apos;re in!</h2>
      <p className="mt-2 text-sm text-ash">
        Your account has been created. Let&apos;s set up your{" "}
        <span className="font-medium text-foreground">{selectedRole.title}</span>{" "}
        {isInstitute ? 'registration.' : 'profile.'}
      </p>

      <div className={`mt-6 rounded-xl border-2 p-5 ${selectedRole.color}`}>
        <div className="flex items-center gap-3">
          <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${selectedRole.iconBg}`}>
            <Icon className={`h-5 w-5 ${selectedRole.iconColor}`} />
          </div>
          <div className="text-left">
            <p className="text-sm font-semibold text-foreground">
              {isInstitute ? 'Next: Registration review' : 'Next: Complete your profile'}
            </p>
            <p className="text-xs text-ash">
              {role === 'employer'
                ? 'Add your company details and post your first job'
                : role === 'shore'
                ? 'Add your experience and start getting matched'
                : role === 'institute'
                ? 'Your institute registration will be reviewed by our team'
                : 'Add your rank, sea service and certifications'}
            </p>
          </div>
        </div>
      </div>

      <button
        onClick={() => router.push(nextHref)}
        className="mt-6 flex w-full items-center justify-center gap-2 rounded-lg bg-primary py-3 text-sm font-semibold text-obsidian hover:bg-primary/90"
      >
        {ctaLabel}
        <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
}

function ProgressDots({ step }: { step: 1 | 2 | 3 }) {
  return (
    <div className="flex items-center justify-center gap-2 mb-8">
      {[1, 2, 3].map((s) => (
        <div
          key={s}
          className={`rounded-full transition-all duration-300 ${
            s === step
              ? 'h-2.5 w-8 bg-primary'
              : s < step
              ? 'h-2.5 w-2.5 bg-primary/40'
              : 'h-2.5 w-2.5 bg-panel'
          }`}
        />
      ))}
    </div>
  );
}

export function RegisterForm({
  selectedRole: controlledRole,
  onRoleChange,
}: {
  selectedRole?: Role | null;
  onRoleChange?: (role: Role | null) => void;
} = {}) {
  const isControlled = controlledRole !== undefined;
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const activeRole = isControlled ? controlledRole ?? null : selectedRole;

  useEffect(() => {
    if (!isControlled) return;
    setStep(controlledRole ? 2 : 1);
  }, [controlledRole, isControlled]);

  const handleRoleSelect = (role: Role) => {
    if (isControlled) {
      onRoleChange?.(role);
    } else {
      setSelectedRole(role);
    }
    setStep(2);
  };

  const handleBack = () => {
    if (isControlled) {
      onRoleChange?.(null);
    }
    setStep(1);
  };

  const handleSuccess = () => {
    setStep(3);
  };

  return (
    <div>
      <ProgressDots step={step} />

        {step === 1 && !isControlled && <RoleStep selectedRole={activeRole} onSelect={handleRoleSelect} />}
        {step === 2 && activeRole && (
          <DetailsStep
            role={activeRole}
            onBack={handleBack}
            onSuccess={handleSuccess}
          />
        )}
        {step === 3 && activeRole && <SuccessStep role={activeRole} />}
    </div>
  );
}

export default RegisterForm;
